import {SiteType} from 'src/app/types/site.type';
import {
  AccSolutionRowState,
  ProductStatus,
  SiteSearchEntryDto,
  SubscriptionBillingCycleType,
  UwV2DashboardProductsUsageStatsListParams,
} from '../../../userway/sdk/UserWayApiClient';
import {TeamMemberDto} from '../../../userway/sdk/SSOApiClient';

export interface IWidgetPosition {
  position: string;
  positionNumber: number;
}

export interface IMySitesSearchRequest {
  p?: number | string;
  q?: number | string;
  s?: string;
  referrer?: number;
  preselect?: number[];
  d?: string;
  pr?: string;
  a?: string;
  t?: string;
  type?: SiteType;
  siteIds?: any;
  products?: any;
  noProducts?: any;
  isPdfRequest?: boolean;
  status?: 'active' | 'removed' | 'pending' | string;
}

export interface IDownloadCsvRequestParams {
  [key: string]: string;
}

export interface IButtonSettingsItem {
  name: string;
  value: string;
}

export interface IButtonSettings {
  global: boolean;
  items: IButtonSettingsItem[];
  host: string;
}

export interface IButtonSize {
  sizeName: string;
  width: number;
  textButtonWidth?: number;
  textButtonHeight?: number;
}

export interface IChangeEmailData {
  email: string;
  password: string;
}

export interface IUsageStatsExportCSVRequest extends UwV2DashboardProductsUsageStatsListParams {
  email?: string;
}

export interface IUsageStatsExportCSVResponse {
  payload: {
    code: number;
  };
  payloadType: string;
}

export interface IPaymentEstimate {
  paymentProvider: string;
  paymentProviderSubscriptionId: number;
}

export interface IChangeProduct {
  productType: string;
}

export interface IGetServiceTypeById {
  userTariff: number;
  id: string;
  adminTariff: number;
  name: string;
  title: string;
}

export interface IMyDomains {
  domains: IDomainsItems[];
}

export interface ICbLogos {
  logos: string[];
}

export interface IDomainsItems {
  group: null;
  host: string;
  id: number;
  loads: number;
  removed: boolean;
  url: string;
  selected?: boolean;
}

export interface ICurrentSiteInfo {
  url: string;
  siteName: string;
  siteId: number;
}

export interface IAddNewSite {
  siteName: string;
}

export interface IPendingSite {
  siteUrl: string;
}

export interface IAddNewSitesBatch {
  siteNames: string[];
}
export interface IAddNewSitesAgencyBatch {
  siteNames: string[];
  siteState: string;
}

export interface IWidgetDefaultState {
  accessibility_link_enabled: string;
  accessibility_link_text: string;
  accessibility_url: string;
  site_name: string;
  widget_color: string;
  paidAi?: boolean;
  widget_color_gradient: string;
  widget_hidden_for_site: string;
  widget_hidden_for_mobile: boolean;
  widget_sounds: boolean;
  widget_icon_size: string;
  widget_icon_type: string;
  widget_language: string;
  widget_position: string;
  widget_position_mobile: string;
  widget_custom_trigger: string;
  widget_custom_trigger_enabled: boolean;
  site_id: number;
  host: string;
  widget_position_nudge?: string;
  widget_position_nudge_mobile?: string;
  widget_live_language_enabled?: boolean;
}

export interface IMainSites {
  moreAvailable: boolean;
  pageIdx: number;
  perPage: number;
  searchPhrase: string;
  sites: SiteSearchEntryDto[];
  total?: number;
  sitesTotal?: number;
}

export interface IMySitesList {
  host: string;
  id: number;
  url: string;
  created: string;
  suspended: boolean;
  loadsPast30Days?: number;
  forcedLoadsPast30Days: number;
  whiteLabelId?: number;
  remediationId?: number;
  remediationProPlus: boolean;
  customBrandingId?: number;
  usageStatsId?: number;
  customMenuId?: number;
  paidAsAgency?: boolean;
  liveTranslationId?: number;
  isPrepaid?: boolean;
  whiteLabelAgencyFree?: boolean;
  customBrandingAgencyFree?: boolean;
  addedByAgency?: boolean;
  remediationBillingCycleMonths?: number;
  managedAccessibility?: boolean;
  primarySiteId?: number;
  viewInfo: {
    url: string;
    traffic: number;
    dateAdded: string;
    billingCycle: SubscriptionBillingCycleType | null;
    product: ProductStatus;
    accScore: number | AccSolutionRowState;
  };
  pdfInfo?: {
    total: number;
    inaccessible: number;
    accessible: number;
    status: RemediationOfSitePdfsStatus;
  };
  platformApp: string;
  platformRedirectLink?: string;
  isPlatformInitialPurchase: boolean;
}

export enum RemediationOfSitePdfsStatus {
  NONE = 'NONE',
  REMEDIATION_IN_PROGRESS = 'REMEDIATION_IN_PROGRESS',
  REMEDIATED = 'REMEDIATED',
  ALL_UNAVAILABLE = 'ALL_UNAVAILABLE',
}

export interface TeamPicture {
  content: string;
  filename: string;
}

export interface IUnitDto {
  id: string;
  accountCode: string;
  slug: string;
  name: string;
  icon: string;
  label: string;
  teamMembers: ITeamMemberResponse;
  teamMembersCount: number;
}

export interface ITeamGroup {
  allSites?: boolean;
  name: string;
  sites?: number[];
  members?: string[];
}

export interface ITeamMemberResponse {
  teamImageUri: string;
  teamImageUrl: string;
  teamMembers: TeamMemberDto[];
  teamName: string;
  teamId: string;
  mfaRequired: boolean;
}

export interface INumbersImages {
  ai_generated_alts: number;
  decorative: number;
  total: number;
  with_alts: number;
  without_alts: number;
}

export interface IUiActionEvent {
  event: string;
  timestamp: any;
  content: string;
  uId: string;
  page: string;
  page_number?: number;
  tab?: string;
  device: string;
  site: string;
  personate: boolean;
  userway_user_id: string;
  userway_user_name?: string;
  userway_user_email?: string;
}

export interface IAccStatement {
  id?: number;
  title: string;
  content: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IUpgradeSubPlanRequest {
  subscriptionId: number;
  paymentMethodId: string;
}

export enum Platforms {
  Windows = 'Windows',
  MacOS = 'MacOS',
  Linux = 'Linux',
  Android = 'Android',
  iOS = 'iOS',
}

export enum CsvExportType {
  Site = 'site',
  Stats = 'stats',
  ImgAlt = 'img-alt',
  Pdfs = 'Pdfs',
  LST = 'LST',
}

export interface UserInfoEventData {
  userway_user_id: string;
  userway_user_name: string;
  userway_user_email: string;
}

export interface SiteDetailsUrlParams {
  ownerId: string | null;
  siteName: string | null;
}
